import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { logger } from '@/common/api/logger';
import type { ConnectToAgent } from '@/common/bot/types/config';

export function isAbleToConnectToAgent(
  connectConfig?: Partial<ConnectToAgent> | null
): connectConfig is ConnectToAgent {
  return Boolean(
    connectConfig &&
    connectConfig.connect &&
    (connectConfig.liveChat || connectConfig.openRequest || connectConfig.voiceChat) &&
    (connectConfig.abTestingPassed ?? true)
  );
}

/**
 * Store for managing global connect configuration.
 * Provides centralized access to connect-to-agent configuration
 * that can be shared across different components and flows.
 */
export const useConnectConfig = defineStore('connectConfig', () => {
  const connectConfig = ref<ConnectToAgent | null>(null);

  /**
   * Check if connect configuration is set
   */
  const isConfigSet = computed(() => connectConfig.value !== null);
  const canConnectToAgent = computed(() => {
    return isAbleToConnectToAgent(connectConfig.value);
  });

  /**
   * Update the connect configuration
   * @param config - The new connect configuration to set
   */
  function setConnectConfig(config: ConnectToAgent | null) {
    connectConfig.value = config;
  }

  /**
   * Update partial connect configuration (merge with existing)
   * @param partialConfig - Partial config to merge with current config
   */
  function updateConnectConfig(partialConfig: Partial<ConnectToAgent>) {
    if (connectConfig.value) {
      connectConfig.value = {
        ...connectConfig.value,
        ...partialConfig,
      };
    } else {
      // If no config exists, you might want to handle this case
      // For now, we'll just log a warning
      logger.warn('Cannot update config: no existing config found');
    }
  }

  /**
   * Clear the connect configuration
   */
  function clearConnectConfig() {
    connectConfig.value = null;
  }

  /**
   * Check if a specific property exists in the config
   * @param key - The property key to check
   */
  function hasConfigProperty<K extends keyof ConnectToAgent>(key: K): boolean {
    return connectConfig.value !== null && key in connectConfig.value;
  }

  /**
   * Get a specific property from the config
   * @param key - The property key to get
   */
  function getConfigProperty<K extends keyof ConnectToAgent>(key: K): ConnectToAgent[K] | undefined {
    return connectConfig.value?.[key];
  }

  return {
    // State
    connectConfig,

    // Getters
    isConfigSet,
    canConnectToAgent,

    // Actions
    setConnectConfig,
    updateConnectConfig,
    clearConnectConfig,
    hasConfigProperty,
    getConfigProperty,
  };
});
